import { Anchor, Stack, Text } from '@mantine/core';
import { formatDate } from '@medplum/core';
import type {
  Attachment,
  CodeableConcept,
  Coding,
  Quantity,
  QuestionnaireResponse,
  QuestionnaireResponseItem,
  QuestionnaireResponseItemAnswer,
  Range,
} from '@medplum/fhirtypes';
import type { JSX } from 'react';

interface CustomQuestionnaireResponseDisplayProps {
  questionnaireResponse: QuestionnaireResponse;
}

export const CustomQuestionnaireResponseDisplay = ({
  questionnaireResponse,
}: CustomQuestionnaireResponseDisplayProps): JSX.Element => {
  return (
    <Stack gap={0}>
      {questionnaireResponse?.item?.map((item, index) => (
        <QuestionnaireResponseItemDisplay item={item} key={`item-${item.id ?? index}`} />
      ))}
    </Stack>
  );
};

interface QuestionnaireResponseItemDisplayProps {
  item: QuestionnaireResponseItem;
}

function QuestionnaireResponseItemDisplay({ item }: QuestionnaireResponseItemDisplayProps): JSX.Element | null {
  const { text: title, answer, item: nestedAnswers } = item;

  const hasAnswer = answer && answer.length > 0;
  const hasNested = nestedAnswers && nestedAnswers.length > 0;

  if (!title && !hasAnswer && !hasNested) {
    return null;
  }

  let renderContent: JSX.Element;
  if (hasAnswer) {
    renderContent = (
      <>
        {answer.map((ans, index) => (
          <AnswerDisplay answer={ans} key={`answer-${index}`} />
        ))}
      </>
    );
  } else if (hasNested) {
    renderContent = (
      <>
        {nestedAnswers.map((nestedAnswer, index) => (
          <QuestionnaireResponseItemDisplay item={nestedAnswer} key={`nested-${nestedAnswer.id ?? index}`} />
        ))}
      </>
    );
  } else {
    renderContent = <Text c="dimmed">No answer</Text>;
  }

  return (
    <Stack gap={0} pb="xs">
      <Text size="lg" fw={600} id={item.id ? `question-${item.id}` : undefined} component="h3">
        {title}
      </Text>
      {renderContent}
    </Stack>
  );
}

interface AnswerDisplayProps {
  answer: QuestionnaireResponseItemAnswer;
}

function AnswerDisplay({ answer }: AnswerDisplayProps): JSX.Element {
  if (!answer) {
    return <Text c="dimmed">Invalid answer</Text>;
  }

  const validEntries = Object.entries(answer).filter(([, value]) => value !== null);
  if (validEntries.length === 0) {
    return <Text c="dimmed">No valid answer data</Text>;
  }

  const [key, value] = validEntries[0] as [string, unknown];

  switch (key) {
    case 'valueInteger':
      return <Text>{value as number}</Text>;
    case 'valueQuantity':
      return <QuantityDisplay value={value as Quantity} />;
    case 'valueString':
      return <Text>{value as string}</Text>;
    case 'valueCoding':
      return <CodeableConceptDisplay value={{ coding: [value as Coding] }} />;
    case 'valueRange':
      return <RangeDisplay value={value as Range} />;
    case 'valueDateTime':
      return <Text>{formatDate(value as string)}</Text>;
    case 'valueBoolean':
      return <Text>{(value as boolean) ? 'True' : 'False'}</Text>;
    case 'valueAttachment': {
      const attachment = value as Attachment;
      const url = attachment.url;
      if (!url) {
        return <Text c="dimmed">No attachment URL</Text>;
      }
      return (
        <div>
          <Anchor href={url} target="_blank" rel="noopener noreferrer" mb="xs">
            Open in new tab
          </Anchor>
        </div>
      );
    }
    case 'valueReference': {
      const ref = value as { display?: string; reference?: string };
      return <Text>{ref.display ?? ref.reference}</Text>;
    }
    default:
      return <Text>{String(value)}</Text>;
  }
}

function QuantityDisplay({ value }: { value: Quantity }): JSX.Element {
  const parts = [value.value, value.unit ?? value.code].filter(Boolean);
  return <Text>{parts.join(' ')}</Text>;
}

function CodeableConceptDisplay({ value }: { value: CodeableConcept }): JSX.Element {
  const display = value.text ?? value.coding?.[0]?.display ?? value.coding?.[0]?.code ?? '';
  return <Text>{display}</Text>;
}

function RangeDisplay({ value }: { value: Range }): JSX.Element {
  const low = value.low?.value;
  const high = value.high?.value;
  const unit = value.low?.unit ?? value.high?.unit ?? value.low?.code ?? value.high?.code;

  if (low !== undefined && high !== undefined) {
    return <Text>{`${low} - ${high}${unit ? ' ' + unit : ''}`}</Text>;
  }
  if (low !== undefined) {
    return <Text>{`≥ ${low}${unit ? ' ' + unit : ''}`}</Text>;
  }
  if (high !== undefined) {
    return <Text>{`≤ ${high}${unit ? ' ' + unit : ''}`}</Text>;
  }
  return <Text c="dimmed">No range data</Text>;
}
